/* eslint-disable perfectionist/sort-imports */
import 'src/global.css';

// i18n
import 'src/locales/i18n';
// ----------------------------------------------------------------------

import Router from 'src/routes/sections';
import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
import ThemeProvider from 'src/theme';
import { LocalizationProvider } from 'src/locales';
import ProgressBar from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
import { SettingsDrawer, SettingsProvider } from 'src/components/settings';

import { ApolloProvider } from '@apollo/client';

import { CheckoutProvider } from 'src/sections/checkout/context';

import { AuthProvider } from 'src/auth/context/apollo';
import { NextStepReact, NextStepProvider } from 'nextstepjs';
import { client } from './apollo';
import { steps, WizardCard, useWizardNavigationAdapter } from './components/wizard';

// ----------------------------------------------------------------------

const LOYALTY_TOUR_SELECTORS = [
  '#loyalty-program-card',
  '#loyalty-public-page-card',
  '#loyalty-rules-tab',
  '#loyalty-tiers-tab',
  '#loyalty-rewards-tab',
  '#loyalty-redemptions-tab',
];

function handleWizardStepChange(step: number, tourName: string | null) {
  if (tourName !== 'loyaltyTour') return;

  window.requestAnimationFrame(() => {
    document.querySelector(LOYALTY_TOUR_SELECTORS[step])?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
    });
  });
}

// ----------------------------------------------------------------------

export default function App() {
  useScrollToTop();

  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <LocalizationProvider>
          <SettingsProvider
            defaultSettings={{
              themeMode: 'light', // 'light' | 'dark'
              themeDirection: 'ltr', //  'rtl' | 'ltr'
              themeContrast: 'default', // 'default' | 'bold'
              themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
              themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
              themeStretch: false,
            }}
          >
            <ThemeProvider>
              <MotionLazy>
                <SnackbarProvider>
                  <CheckoutProvider>
                    <SettingsDrawer />
                    <ProgressBar />
                    <NextStepProvider>
                      <NextStepReact
                        steps={steps}
                        shadowOpacity="0.5"
                        noInViewScroll
                        cardComponent={WizardCard}
                        onStepChange={handleWizardStepChange}
                        navigationAdapter={useWizardNavigationAdapter}
                      >
                        <Router />
                      </NextStepReact>
                    </NextStepProvider>
                  </CheckoutProvider>
                </SnackbarProvider>
              </MotionLazy>
            </ThemeProvider>
          </SettingsProvider>
        </LocalizationProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}
